import $ from 'jquery';
import _ from 'underscore';
import Backbone from "backbone";
import Chaplin from "chaplin";
import ExportPopupView from "views/audioEditor/ExportPopupView";
import ProgressBarController from "controllers/common/ProgressBarController";
import SequencerExportHelper from "models/audioEditor/SequencerExportHelper";
import SequencerRendererHelper from "models/audioEditor/SequencerRendererHelper";
import SequencerRendererUtils from "models/audioEditor/SequencerRendererUtils";
import Utils from "models/helper/Utils";
import TrebbleClientAPIHelper from "models/helper/TrebbleClientAPI";
import AudioBufferHelper from "models/audioEditor/AudioBufferHelper";
import FacebookEventTrackingHelper from "models/helper/FacebookEventTrackingHelper";
import MixpanelHelper from "models/helper/MixpanelHelper";
import ExpectedError, {
    EXPECTED_ERROR_CATEGORY,
    EXPECTED_ERROR_CLASSIFICATION,
} from "models/helper/ExpectedError";
import ti18n from "i18n!nls/ExportPopupi18n";
import RSVP from "rsvp";

/* global USE_TIMELOOM_FOR_EXPORT */

const ExportPopupController = Chaplin.Controller.extend({

    initialize: function(options) {
        _.extend(this, Backbone.Events);
        this._sequencerController = options.sequencerController;
        this._hasVideo = this._sequencerController? this._sequencerController.getModel().getHasVideoSegment() : options.hasVideo || false;
        this._selection = options.selection || null;
        this._projectId = options.projectId || null; // For project-based exports
        this._customTitle = options.customTitle || null; // Custom title for exports (e.g., from capsule)
        this._videoUrl = options.videoUrl || null; // Pre-rendered video URL for project-list exports
        this._isSelectionBased = !!(this._selection || (this._sequencerController && this._sequencerController.getView().getSelectionNode()));
        this._capsuleModel = options.capsuleModel || null; // For capsule-based exports with pre-rendered files
        this._isSequencerAlreadyInstantiated = !this._capsuleModel; // Flag to distinguish sequencer vs capsule context
        this._progressBarController = null; // Will be created in buildModelAndRenderView
    },

    getModel: function() {
        return this.model;
    },

    getView: function() {
        return this.view;
    },

    onPopupClosed: function() {
        // Cleanup progress bar controller if it exists
        if (this._progressBarController) {
            this._progressBarController = null;
        }
        
        // Cleanup any ongoing export operations
        if (this._currentExportPromise) {
            this._currentExportPromise = null;
        }
    },

    /**
     * Get video export strategy based on feature flags and options
     * @private
     * @param {Object|String} sequencerOrProjectId - Sequencer instance or project ID
     * @param {Object} exportOptions - Export configuration options
     * @returns {Object} Strategy object with method and options/url
     */
    _getVideoExportStrategy: function(sequencerOrProjectId, exportOptions) {
        const useTimeloomRendering = typeof USE_TIMELOOM_FOR_EXPORT !== 'undefined' && USE_TIMELOOM_FOR_EXPORT;
        const exportHelper = SequencerExportHelper.getInstance();
        const videoUrl = this._getVideoUrl(sequencerOrProjectId, exportOptions);

        if (videoUrl) {
            return {
                method: 'legacy',
                url: videoUrl,
                options: exportOptions
            };
        }

        // Check if Timeloom export method exists
        if (useTimeloomRendering && typeof exportHelper.exportVideoWithTimeloom !== 'function') {
            console.warn('[ExportPopupController] Timeloom export not available, falling back to legacy');
            // Fall back to legacy export
            return {
                method: 'legacy',
                url: null,
                options: exportOptions
            };
        }

        if (useTimeloomRendering) {
            // Modern Timeloom/Remotion rendering
            // Preserve all export options and override only Timeloom-specific defaults
            const timeloomOptions = {
                ...exportOptions, // Preserve format, includeAudio, watermark, filename, etc.
                aspectRatio: exportOptions.aspectRatio || '16:9',
                frameRate: exportOptions.frameRate || 25,
                videoTierResolution: exportOptions.videoTierResolution || 'medium',
                backgroundColor: exportOptions.backgroundColor || '#000000'
            };

            // Add startNode/endNode only if present (for selections)
            if (exportOptions.startNode) {
                timeloomOptions.startNode = exportOptions.startNode;
            }
            if (exportOptions.endNode) {
                timeloomOptions.endNode = exportOptions.endNode;
            }

            return {
                method: 'timeloom',
                options: timeloomOptions
            };
        } else {
            // Legacy FFmpeg cloud rendering
            return {
                method: 'legacy',
                url: null,
                options: exportOptions
            };
        }
    },

    /**
     * Handle export action from React component
     */
    handleExport: async function(exportOptions) {
        const exportType = exportOptions.type || 'unknown';
        const exportFormat = exportOptions.format || 'unknown';
        const exportSource = this._isSequencerAlreadyInstantiated ? 'editor' : 'library';
        const exportStartTime = Date.now();

        // Track export initiated (best-effort, must not block export)
        try {
            if(window.trebbleAnalyticsHelper){
                window.trebbleAnalyticsHelper.trackEvent("export", "ExportStarted", "Export Started", null, {"type": exportType, "format": exportFormat, "source": exportSource, "hasVideo": this._hasVideo, "isSelection": this._isSelectionBased});
            }
        } catch(analyticsError) {
            console.warn('Failed to track ExportStarted:', analyticsError);
        }

        try {
            // Show progress bar with initial message
            this.showProgressBar(window.getI18n(ti18n, 'PREPARING_EXPORT'));

            if (!this._isSequencerAlreadyInstantiated) {
                // Capsule-based export: use pre-rendered files when available (sequencer not instantiated)
                this._currentExportPromise = this._performCapsuleExport(exportOptions, this._createProgressCallback());
                await this._currentExportPromise;
            } else {
                // Sequencer-based export: generate files on-the-fly (sequencer already instantiated)
                this._currentExportPromise = this._performSequencerExport(exportOptions, this._createProgressCallback());
                await this._currentExportPromise;
            }

            const exportDurationMs = Date.now() - exportStartTime;

            // Track successful export (best-effort, must not block success flow)
            try {
                if(window.trebbleAnalyticsHelper){
                    window.trebbleAnalyticsHelper.trackEvent("export", "ExportCompleted", "Export Completed", null, {"type": exportType, "format": exportFormat, "source": exportSource, "durationMs": exportDurationMs, "hasVideo": this._hasVideo, "isSelection": this._isSelectionBased});
                }
            } catch(analyticsError) {
                console.warn('Failed to track ExportCompleted:', analyticsError);
            }
            try {
                MixpanelHelper.getInstance().people.increment({"totalExports": 1});
            } catch(incrementError) {
                console.warn('Failed to increment totalExports:', incrementError);
            }
            try {
                FacebookEventTrackingHelper.getInstance().trackAdvancedFeatureUsed(`export_${exportType}_${exportFormat}`);
            } catch(trackingError) {
                console.warn('Failed to track export:', trackingError);
            }

            // Show success message
            window.alertSuccessMessage(window.getI18n(ti18n, "EXPORT_COMPLETE"));

        } catch (error) {
            // Hide progress bar on error
            this.hideProgressBar();
            console.error('Export error:', error);

            const exportDurationMs = Date.now() - exportStartTime;

            // Track export failure (best-effort, must not mask original error)
            try {
                if(window.trebbleAnalyticsHelper){
                    const errorCategory =
                        (typeof error === "string") ? "string_error" :
                        (typeof error?.code === "string" && error.code) ? error.code :
                        (error && error.name) ? error.name : "unknown";
                    window.trebbleAnalyticsHelper.trackEvent("export", "ExportFailed", "Export Failed", null, {"type": exportType, "format": exportFormat, "source": exportSource, "durationMs": exportDurationMs, "errorCategory": errorCategory, "hasVideo": this._hasVideo, "isSelection": this._isSelectionBased});
                }
            } catch(analyticsError) {
                console.warn('Failed to track ExportFailed:', analyticsError);
            }

            if (
                error instanceof ExpectedError &&
                error.expectedErrorCategory === EXPECTED_ERROR_CATEGORY.USER_ABORTED_BROWSER_OPERATION
            ) {
                if (window.trackExpectedErrorSkippedRollbar) {
                    window.trackExpectedErrorSkippedRollbar(error, "export");
                }
                return;
            }

            window.alertErrorMessage(
                window.getI18n(ti18n, "EXPORT_FAILED"),
                error.message || window.getI18n(ti18n, "UNKNOWN_ERROR")
            );
            // Clean up the export promise on error
            this._currentExportPromise = null;
            throw error; // Re-throw for the view to handle
        } finally {
            // Ensure cleanup happens regardless of success/failure
            this._currentExportPromise = null;
        }
    },

    /**
     * Create progress callback that uses controller's progress management
     */
    _createProgressCallback: function() {
        return (percentage, message) => {
            this.updateProgress(percentage, message);
        };
    },

    /**
     * Show progress bar via view and configure progress bar controller
     */
    showProgressBar: function(message) {
        if (this.view) {
            this.view.showProgressBar(message);
        }
        if (this._progressBarController) {
            this._progressBarController.setIndeterminate(true);
            if (message) {
                this._progressBarController.setProgressMessage(message);
            }
        }
        return RSVP.Promise.resolve();
    },

    /**
     * Hide progress bar via view
     */
    hideProgressBar: function() {
        if (this.view) {
            this.view.hideProgressBar();
        }
    },

    /**
     * Update progress via progress bar controller
     */
    updateProgress: function(percentage, message) {
        if (percentage === 'HIDE_EXTERNAL_PROGRESS') {
            this.hideProgressBar();
            return;
        }
        
        if (this._progressBarController) {
            if (typeof percentage === 'number') {
                this._progressBarController.setPercentageProgress(percentage);
            } else {
                this._progressBarController.setIndeterminate(true);
            }
            if (message) {
                this._progressBarController.setProgressMessage(message);
            }
        }
    },

    /**
     * Perform capsule-based export using pre-rendered files when available
     */
    _performCapsuleExport: async function(exportOptions, progressCallback) {
        const format = exportOptions.format;
        const filename = `${this._capsuleModel.getTitle()}.${format}`;

        // Report initial progress to trigger progress bar display
        if (progressCallback) {
            progressCallback(null, window.getI18n(ti18n, 'PREPARING_EXPORT'));
        }

        switch (exportOptions.type) {
            case 'audio': {
                // Use pre-rendered audio files from capsule
                const audioUrl = format === 'wav' 
                    ? this._capsuleModel.getAudioHQUrl(true) // Get WAV if available
                    : this._capsuleModel.getAudioUrl(); // Get MP3
                
                if (!audioUrl) {
                    // Fallback to on-the-fly generation if no pre-rendered file
                    if (progressCallback) {
                        progressCallback(null, window.getI18n(ti18n, 'GENERATING_AUDIO_FILE'));
                    }
                    await this._generateAudioFromProject(exportOptions, progressCallback);
                } else {
                    if (progressCallback) {
                        progressCallback(null, window.getI18n(ti18n, 'DOWNLOADING_AUDIO_FILE'));
                    }
                    await this._downloadPreRenderedFile(audioUrl, filename, progressCallback);
                    this._trackCapsuleDownload('audio', audioUrl);
                }
                break;
            }
            
            case 'video': {
                const videoUrl = this._capsuleModel.getVideoUrl();
                if (!videoUrl) {
                    throw new Error(window.getI18n(ti18n, "VIDEO_NOT_READY_WARNING"));
                }
                if (progressCallback) {
                    progressCallback(null, window.getI18n(ti18n, 'DOWNLOADING_VIDEO_FILE'));
                }
                await this._downloadPreRenderedFile(videoUrl, filename, progressCallback);
                this._trackCapsuleDownload('videoDownload', videoUrl);
                break;
            }
            
            case 'transcript':
            case 'subtitles':
            case 'timeline': {
                // These always need to be generated from project data
                if (progressCallback) {
                    progressCallback(null, window.getI18n(ti18n, 'GENERATING_FROM_PROJECT_DATA'));
                }
                await this._generateFromProject(exportOptions, progressCallback);
                break;
            }
            
            default:
                throw new Error('Unknown export type: ' + exportOptions.type);
        }
    },

    /**
     * Perform sequencer-based export using on-the-fly generation
     */
    _performSequencerExport: async function(exportOptions, progressCallback) {
        // Report initial progress to trigger progress bar display
        if (progressCallback) {
            progressCallback(null, window.getI18n(ti18n, 'PREPARING_EXPORT'));
        }

        let sequencerOrProjectId;
        let exportParams = { ...exportOptions };
        const sequencerController = this._sequencerController;
        const sequencer = sequencerController && sequencerController.getModel
            ? sequencerController.getModel()
            : null;
        const needsSequencer = this._isSelectionBased || !this._projectId;

        if (needsSequencer && !sequencer) {
            throw new ExpectedError(window.getI18n(ti18n, "EXPORT_CANCELLED"), {
                i18nKey: "EXPORT_CANCELLED",
                classification: EXPECTED_ERROR_CLASSIFICATION.NAVIGATION,
                errorCode: "EXPORT_CANCELLED",
                expectedErrorCategory: EXPECTED_ERROR_CATEGORY.USER_ABORTED_BROWSER_OPERATION,
            });
        }

        if (this._isSelectionBased) {
            // Selection-based export: use sequencer + start/end nodes
            const selection = this._getSelection();
            
            if (!selection || !selection.startSequencerNodeModel || !selection.endSequencerNodeModel) {
                const noSelectionError = new Error(window.getI18n(ti18n, "NO_SELECTION_ERROR"));
                noSelectionError.name = "NoSelectionError";
                throw noSelectionError;
            }

            sequencerOrProjectId = sequencer;
            exportParams.startNode = selection.startSequencerNodeModel;
            exportParams.endNode = selection.endSequencerNodeModel;
        } else {
            // Project-based export: use projectId (loads project as needed)
            sequencerOrProjectId = this._projectId || sequencer.getProjectId();
        }

        // Perform export using the refactored helper with progress callback
        await this._performExportWithHelper(sequencerOrProjectId, exportParams, progressCallback);
    },

    /**
     * Download pre-rendered file with progress tracking
     * Uses AudioBufferCache when Sequencer is available for optimized loading
     */
    _downloadPreRenderedFile: async function(url, filename, progressCallback) {
        try {
            // Check if we have access to a Sequencer for AudioBufferCache optimization
            const sequencer = this._getSequencerIfAvailable();
            
            if (sequencer) {
                // Use AudioBufferCache for optimized loading when Sequencer is available
                await this._downloadFileViaAudioBufferCache(url, filename, sequencer, progressCallback);
            } else {
                // Fallback to regular download approach
                await this._downloadFileViaRegularFetch(url, filename, progressCallback);
            }
            
            // Report completion
            if (progressCallback) {
                progressCallback(100, window.getI18n(ti18n, 'DOWNLOAD_COMPLETE'));
            }
            
        } catch (error) {
            console.error('Download failed:', error);
            // Fallback to regular download if AudioBufferCache fails
            if (error.message && error.message.includes('AudioBufferCache')) {
                console.log('Falling back to regular download...');
                await this._downloadFileViaRegularFetch(url, filename, progressCallback);
                if (progressCallback) {
                    progressCallback(100, window.getI18n(ti18n, 'DOWNLOAD_COMPLETE'));
                }
            } else {
                throw error;
            }
        }
    },

    /**
     * Get Sequencer instance if available
     */
    _getSequencerIfAvailable: function() {
        // Check if we have a sequencer controller with a model
        if (this._sequencerController && this._sequencerController.getModel) {
            return this._sequencerController.getModel();
        }
        
        // For project-based exports, we might need to get sequencer differently
        // but for now, return null to use fallback approach
        return null;
    },

    /**
     * Download file using AudioBufferCache for optimized URL handling
     */
    _downloadFileViaAudioBufferCache: async function(url, filename, sequencer, progressCallback) {
        try {
            // Report URL optimization progress
            if (progressCallback) {
                progressCallback(10, window.getI18n(ti18n, 'OPTIMIZING_DOWNLOAD_URL'));
            }

            // Use AudioBufferCache to get optimized URL (S3 → CloudFront CDN)
            const audioBufferCache = sequencer.getAudioBufferCache();
            const optimizedUrl = audioBufferCache.getCloudFrontUrlIfApplicable(url);
            
            if (progressCallback) {
                progressCallback(20, window.getI18n(ti18n, 'DOWNLOADING_OPTIMIZED_FILE'));
            }

            // Download using the optimized URL
            await this._downloadFileViaRegularFetch(optimizedUrl, filename, (percentage, message) => {
                if (progressCallback) {
                    // Map download progress to 20-100% of total progress
                    const mappedPercentage = 20 + (percentage * 0.8);
                    progressCallback(mappedPercentage, message);
                }
            });

        } catch (error) {
            throw new Error('AudioBufferCache download failed: ' + error.message);
        }
    },

    /**
     * Regular download approach (fallback)
     */
    _downloadFileViaRegularFetch: async function(url, filename, progressCallback) {
        try {
            const downloadProgressFunction = (progressUnderOne) => {
                if (!progressCallback) return;
                
                if (!Number.isFinite(progressUnderOne)) {
                    progressCallback(null, window.getI18n(ti18n, 'DOWNLOADING'));
                    return;
                }
                
                const percentage = progressUnderOne * 100;
                progressCallback(percentage, window.getI18n(ti18n, 'DOWNLOADING'));
            };
            
            await Utils.getInstance().downloadURI(url, filename, downloadProgressFunction);
        } catch (error) {
            console.error('Download failed:', error);
            const msg = (error && error.message) || '';
            const isNetworkOrCors =
                error?.code === 'CONNECTION_LOST_DOWNLOAD' ||
                (error instanceof TypeError) || 
                /Failed to fetch|CORS|connection issue/i.test(msg);
                
            if (isNetworkOrCors) {
                console.log('Attempting alternative download method...');
                try {
                    Utils.getInstance().downloadViaNavigation(url, filename);
                    
                    if (progressCallback) {
                        progressCallback(100, window.getI18n(ti18n, 'DOWNLOAD_COMPLETE'));
                    }
                } catch (fallbackError) {
                    console.error('Alternative download also failed:', fallbackError);
                    throw new Error(window.getI18n(ti18n, 'CONNECTION_LOST_DOWNLOAD'));
                }
            } else {
                throw error;
            }
        }
    },

    /**
     * Generate audio from project when pre-rendered file not available
     */
    _generateAudioFromProject: async function(exportOptions, progressCallback) {
        const asWav = exportOptions.format === 'wav';
        const filename = `${this._capsuleModel.getTitle()}.${asWav ? 'wav' : 'mp3'}`;

        const audioBlob = await SequencerRendererUtils.getInstance().getRenderedAudioBlob(
            this._capsuleModel.getProjectId(),
            asWav,
            false,
            progressCallback,
            null,
            exportOptions.skipAudioProcessing || false
        );

        Utils.getInstance().downloadFile(audioBlob, filename);
    },

    /**
     * Generate transcript/subtitles/timeline from project data
     */
    _generateFromProject: async function(exportOptions, progressCallback) {
        const projectId = this._capsuleModel.getProjectId();
        
        // Use existing helper methods for generation
        await this._performExportWithHelper(projectId, exportOptions, progressCallback);
    },

    /**
     * Track capsule download for analytics
     */
    _trackCapsuleDownload: function(downloadType, url) {
        try {
            TrebbleClientAPIHelper.getInstance().trackCapsuleDownload(
                this._capsuleModel.getCapsuleId(), 
                url, 
                downloadType, 
                this._capsuleModel.getProjectId()
            );
        } catch (error) {
            console.error('Failed marking download. Error: ' + error);
        }
    },

    /**
     * Perform export using the refactored SequencerExportHelper with explicit methods
     */
    _performExportWithHelper: async function(sequencerOrProjectId, exportOptions, progressCallback) {
        const exportHelper = SequencerExportHelper.getInstance();
        const projectTitle = this._getProjectTitle(sequencerOrProjectId);

        if (this._isSelectionBased) {
            // Selection-based export: use sequencer methods
            const sequencer = sequencerOrProjectId;
            
            switch (exportOptions.type) {
                case 'audio': {
                    // Transform export options to match SequencerExportHelper's expected format
                    const audioOptions = {
                        asWav: exportOptions.format === 'wav',
                        startNode: exportOptions.startNode,
                        endNode: exportOptions.endNode,
                        skipAudioProcessing: exportOptions.skipAudioProcessing || false
                    };
                    return exportHelper.exportAudioFromSequencer(sequencer, projectTitle, audioOptions, progressCallback);
                }
                
                case 'video': {
                    const strategy = this._getVideoExportStrategy(sequencer, exportOptions);
                    const sequencerController = this._sequencerController;

                    if (strategy.method === 'timeloom') {
                        return exportHelper.exportVideoWithTimeloom(sequencer, projectTitle, strategy.options, progressCallback, sequencerController);
                    } else {
                        return exportHelper.exportVideo(sequencer, projectTitle, strategy.url, strategy.options, progressCallback, sequencerController);
                    }
                }
                
                case 'transcript':
                    if (exportOptions.format === 'docx') {
                        return exportHelper.exportTranscriptDocxFromSequencer(sequencer, projectTitle, exportOptions, progressCallback);
                    } else {
                        return exportHelper.exportTranscriptTxtFromSequencer(sequencer, projectTitle, exportOptions, progressCallback);
                    }
                
                case 'subtitles': {
                    // Map subtitles to the SRT export method
                    const srtOptions = { ...exportOptions, asVtt: (exportOptions.format === 'vtt') };
                    return exportHelper.exportTranscriptSrtFromSequencer(sequencer, projectTitle, srtOptions, progressCallback);
                }
                
                case 'timeline':
                    return this._exportTimeline(sequencer, exportOptions, progressCallback);
                
                default:
                    throw new Error('Unknown export type: ' + exportOptions.type);
            }
        } else {
            // Project-based export: use project ID methods
            const projectId = sequencerOrProjectId;
            
            switch (exportOptions.type) {
                case 'audio': {
                    // Transform export options to match SequencerExportHelper's expected format
                    const audioOptions = {
                        asWav: exportOptions.format === 'wav',
                        skipAudioProcessing: exportOptions.skipAudioProcessing || false
                    };
                    return exportHelper.exportAudioFromProject(projectId, projectTitle, audioOptions, progressCallback);
                }
                
                case 'video': {
                    const strategy = this._getVideoExportStrategy(projectId, exportOptions);
                    const sequencerController = this._sequencerController;

                    if (strategy.method === 'timeloom') {
                        return exportHelper.exportVideoWithTimeloom(projectId, projectTitle, strategy.options, progressCallback, sequencerController);
                    } else {
                        const videoUrl = strategy.url;
                        // Use strategy.options for consistency with timeloom branch
                        return exportHelper.exportVideo(projectId, projectTitle, videoUrl, strategy.options, progressCallback, sequencerController);
                    }
                }
                
                case 'transcript':
                    if (exportOptions.format === 'docx') {
                        return exportHelper.exportTranscriptDocxFromProject(projectId, projectTitle, exportOptions, progressCallback);
                    } else {
                        return exportHelper.exportTranscriptTxtFromProject(projectId, projectTitle, exportOptions, progressCallback);
                    }
                
                case 'subtitles': {
                    // Map subtitles to the SRT export method
                    const srtOptions = { ...exportOptions, asVtt: (exportOptions.format === 'vtt') };
                    return exportHelper.exportTranscriptSrtFromProject(projectId, projectTitle, srtOptions, progressCallback);
                }
                
                case 'timeline':
                    return this._exportTimeline(projectId, exportOptions, progressCallback);
                
                default:
                    throw new Error('Unknown export type: ' + exportOptions.type);
            }
        }
    },



    /**
     * Export timeline using SequencerExportHelper with new approach
     */
    _exportTimeline: async function(sequencerOrProjectId, options, progressCallback) {
        const exportHelper = SequencerExportHelper.getInstance();
        const projectTitle = this._getProjectTitle(sequencerOrProjectId);
        
        const exportSettings = {
            projectTitle: projectTitle,
            includeMedia: options.includeMedia,
            frameRate: options.frameRate || 25,
            edlFormat: options.format === 'samplitude' ? 'samplitude' : 'cmx3600',
            createTrackForEachFile: options.createTrackForEachFile !== false,
            startNode: options.startNode || null,
            endNode: options.endNode || null
        };

        if (progressCallback) {
            progressCallback(null, window.getI18n(ti18n, 'EXTRACTING_TIMELINE_DATA'));
        }

        if (this._isSelectionBased) {
            // Selection-based export: use sequencer methods
            const sequencer = sequencerOrProjectId;
            
            if (options.format === 'cmx3600' || options.format === 'samplitude') {
                return exportHelper.exportEDLProject(sequencer, options.startNode, options.endNode, exportSettings, progressCallback);
            } else if (options.format === 'fcpxml') {
                return exportHelper.exportFCPXMLProject(sequencer, options.startNode, options.endNode, exportSettings, progressCallback);
            } else if (options.format === 'premiere') {
                return exportHelper.exportPremiereXMLProject(sequencer, options.startNode, options.endNode, exportSettings, progressCallback);
            } else if (options.format === 'sesx') {
                return exportHelper.exportSESXProject(sequencer, options.startNode, options.endNode, exportSettings, progressCallback);
            } else {
                throw new Error('Unknown timeline format: ' + options.format);
            }
        } else {
            // Project-based export: use SequencerRendererUtils for proper cleanup
            const projectId = sequencerOrProjectId;
            
            return SequencerRendererUtils.getInstance().withSequencer(projectId, async (sequencer) => {
                if (progressCallback) {
                    progressCallback(null, window.getI18n(ti18n, 'EXTRACTING_TIMELINE_DATA'));
                }
                
                if (options.format === 'cmx3600' || options.format === 'samplitude') {
                    return exportHelper.exportEDLProject(sequencer, null, null, exportSettings, progressCallback);
                } else if (options.format === 'fcpxml') {
                    return exportHelper.exportFCPXMLProject(sequencer, null, null, exportSettings, progressCallback);
                } else if (options.format === 'premiere') {
                    return exportHelper.exportPremiereXMLProject(sequencer, null, null, exportSettings, progressCallback);
                } else if (options.format === 'sesx') {
                    return exportHelper.exportSESXProject(sequencer, null, null, exportSettings, progressCallback);
                } else {
                    throw new Error('Unknown timeline format: ' + options.format);
                }
            }, progressCallback);
        }
    },

    /**
     * Get current selection from sequencer view
     */
    _getSelection: function() {
        if (this._selection) {
            return this._selection;
        }
        
        // Get selection from sequencer view
        return this._sequencerController.getView().getSelectionNode();
    },

    /**
     * Get project title from custom title, sequencer, or use default
     */
    _getProjectTitle: function(sequencerOrProjectId) {
        // First, check if we have a custom title (e.g., from capsule)
        if (this._customTitle) {
            return this._customTitle;
        }
        
        // Then try to get title from sequencer
        if (typeof sequencerOrProjectId === 'object' && sequencerOrProjectId.getProjectTitle) {
            return sequencerOrProjectId.getProjectTitle() || 'export';
        } else if (this._sequencerController) {
            return this._sequencerController.getModel().getProjectTitle() || 'export';
        } else {
            return 'export';
        }
    },

    /**
     * Get video URL for video exports
     * Checks if a rendered video already exists for the project
     */
    _getVideoUrl: function(sequencerOrProjectId, options) {
        // Selections always trigger render
        if (options && options.startNode && options.endNode) {
            return null;
        }
        if (this._videoUrl) {
            return this._videoUrl;
        }
        // Sequencer instance path
        if (sequencerOrProjectId && typeof sequencerOrProjectId === 'object' && sequencerOrProjectId.getRenderedVideoUrl) {
            const url = sequencerOrProjectId.getRenderedVideoUrl();
            if (url) return url;
        }
        // Capsule fallback (project-based, pre-rendered)
        if (this._capsuleModel && this._capsuleModel.getVideoUrl) {
            return this._capsuleModel.getVideoUrl() || null;
        }
        return null;
    },

    /**
     * Check if video is ready for capsule-based export
     */
    _isVideoReady: function() {
        if (this._isSequencerAlreadyInstantiated) {
            return true; // For sequencer exports, video generation happens on demand
        }
        
        if (!this._capsuleModel) {
            return false;
        }
        
        // Video is ready if it has a video URL (completed rendering)
        return !!this._capsuleModel.getVideoUrl();
    },

    /**
     * Check if video is currently being rendered
     */
    _isVideoBeingRendered: function() {
        if (this._isSequencerAlreadyInstantiated || !this._capsuleModel) {
            return false;
        }
        
        return this._capsuleModel.isVideoBeingRendered();
    },

    /**
     * Build model and render view
     */
    buildModelAndRenderView: function() {
        const promises = [];

        // Create progress bar controller
        this._progressBarController = new ProgressBarController({});
        promises.push(this._progressBarController.buildModelAndRenderView());

        return RSVP.Promise.all(promises).then((function() {
            const params = {
                hasVideo: this._hasVideo,
                isSelection: this._isSelectionBased, // Pass the selection flag to UI
                isSequencerAlreadyInstantiated: this._isSequencerAlreadyInstantiated, // Pass sequencer context flag
                isVideoReady: this._isVideoReady(), // Pass video readiness state
                isVideoBeingRendered: this._isVideoBeingRendered(), // Pass video rendering state
                onExport: this.handleExport.bind(this),
                i18nModule: ti18n
                // Note: progressBarController now managed by this controller, not passed to view
            };

            this.view = new ExportPopupView(params);
            this.listenTo(this.view, "closed", this.onPopupClosed);
            this.listenTo(this.view, "export", this.handleExport);

            this.view.render();
            
            // Attach progress bar view to the popup view's progress container
            this._attachProgressBarView();

        }).bind(this));
    },

    /**
     * Attach progress bar view to the popup view's progress container
     */
    _attachProgressBarView: function() {
        if (this._progressBarController && this._progressBarController.getView() && this.view) {
            this.view.attachProgressBarView(this._progressBarController.getView());
        }
    }
});

export default ExportPopupController;
